import { ConfigService } from '../../infra/config'
import { BaseRepository } from '../../infra/base.repository'
import type { Appointment } from './appointment'

export class AppointmentsRepository extends BaseRepository<Appointment> {
    constructor(
        private config: ConfigService = new ConfigService(),
        token?: string,
        cache?: any,
    ) {
        super(config.schedulerApi + '/' + 'appointments', token, cache)
    }

    async getNextAppointmentFromPerson(personId: string, time: string = null) {
        if (!time) time = ''
        const { json, statusCode, message } = await this.request.get(
            this.config.schedulerApi +
                '/appointments/next/' +
                personId +
                '/?t=' +
                time +
                '&token=' +
                this.resolvedToken,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async getSugestions(personId: string, scheduleId: string) {
        const { json, statusCode, message } = await this.request.get(
            this.config.schedulerApi +
                '/appointments/sugestions/?scheduleId=' +
                scheduleId +
                '&personId=' +
                personId +
                '&token=' +
                this.resolvedToken,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async changeStatus(appointmentId: string, dto) {
        const { json, statusCode, message } = await this.request.patch(
            this.config.schedulerApi +
                '/appointments/' +
                appointmentId +
                '/status/?token=' +
                this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async comissionsChangeStatus(appointmentItemId: string, active: boolean) {
        const { json, statusCode, message } = await this.request.post(
            this.config.schedulerApi + '/appointments/comissions/status?token=' + this.resolvedToken,
            { id: appointmentItemId, active },
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async setItemInvoice(appointmentItemId: string, invoiceId: string | null) {
        const { json, statusCode, message } = await this.request.patch(
            this.config.schedulerApi +
                '/appointments/items/' +
                appointmentItemId +
                '/invoice/?token=' +
                this.resolvedToken,
            { invoiceId },
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async getComissions(params: { page?: number; from?: Date; to?: Date; query?: string } | any) {
        let queryString = '?'
        for (const key in params) {
            if (params[key]) queryString += key + '=' + params[key] + '&'
        }
        const url =
            this.config.schedulerApi +
            '/appointments/comissions/' +
            queryString +
            'token=' +
            this.resolvedToken
        const { json, statusCode, message } = await this.request.get(url)
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async comissionsUpdate(params?: { appointmentId?: string; invoiceId?: string } | any) {
        let queryString = '?'
        for (const key in params) {
            if (params[key]) queryString += key + '=' + params[key] + '&'
        }
        const url =
            this.config.schedulerApi +
            '/appointments/comissions/update' +
            queryString +
            'token=' +
            this.resolvedToken
        const { json, statusCode, message } = await this.request.get(url)
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async comissionsRecalculate(params: { from: Date; to: Date; comissionSource: string }) {
        let queryString = '?'
        for (const key in params) {
            if (params[key]) queryString += key + '=' + params[key] + '&'
        }
        const url =
            this.config.schedulerApi +
            '/appointments/comissions/recalculate' +
            queryString +
            'token=' +
            this.resolvedToken
        const { json, statusCode, message } = await this.request.get(url)
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async getConsulting(from: Date, to: Date, consultingServicesId, fromCreatedAt = false) {
        const { json, statusCode, message } = await this.request.get(
            this.config.schedulerApi +
                '/appointments/consulting?from=' +
                from +
                '&to=' +
                to +
                '&services=' +
                consultingServicesId +
                '&fromCreatedAt=' +
                fromCreatedAt +
                '&token=' +
                this.resolvedToken,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async publicChangeStatus(personId: string, appointmentId: string, dto) {
        const { json, statusCode, message } = await this.request.patch(
            this.config.schedulerApi +
                '/appointments/next/' +
                personId +
                '/confirm/' +
                appointmentId +
                '/?token=' +
                this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async setPaid(
        appointmentId: string,
        dto: { invoiceId: string; comissionSource: string; voidComission: boolean; status?: string },
    ) {
        const { json, statusCode, message } = await this.request.patch(
            this.config.schedulerApi +
                '/appointments/' +
                appointmentId +
                '/paid/?token=' +
                this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async setUnpaid(appointmentId: string) {
        const { json, statusCode, message } = await this.request.delete(
            this.config.schedulerApi +
                '/appointments/' +
                appointmentId +
                '/paid/?token=' +
                this.resolvedToken,
            {},
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async sign(appointmentId: string, signatureBase64: string) {
        const { json, statusCode, message } = await this.request.post(
            this.config.schedulerApi +
                '/appointments/' +
                appointmentId +
                '/sign/?token=' +
                this.resolvedToken,
            { sign: signatureBase64 },
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }
}
